import React, { FC, useCallback, useRef } from 'react';
import { CarCardType } from '~/data/types/car/cars';
import { Button, Menu, Typography } from '@mui/material';
import RESOURCES from '~/resources';
import { useIsMobile } from '~/hooks/useIsMobile';
import ShareCopyLinkIcon from '~/assets/icons/jsx/shareCopyLinkIcon';
import ShareBtn from '~/assets/icons/jsx/shareBtn';
import useControlBodyOverflow from '~/hooks/useControlBodyOverflow';
import { UsedCarType } from '~/data/types/usedCar/usedCarData';
import { btnTextStyle, openShareBtnStyle, shareBtnStyle } from './useStyles';
import { SHARE_CAR_OPTIONS } from './shareCarButtonData';
import MenuItemDdl from './MenuItemDdl';
import { Toast } from '../Toast/Toast';
import { EActionEvent, ECategoryEvent, useEvents } from '~/hooks/useEvents';
import useLineOfBusinessTarget from '~/hooks/useLineOfBusinessTarget';
import { useIsUsedCar } from '~/hooks/useIsUsedCar';
import { COLORS } from '~/data/ui/colors';

interface ShareButtonProps {
  car: CarCardType | UsedCarType;
}
const ShareCarButton: FC<ShareButtonProps> = ({ car }) => {
  const [openToast, setOpenToast] = React.useState(false);
  const isMobile = useIsMobile();
  const businessLineTarget = useLineOfBusinessTarget();
  const events = useEvents();
  const isUsedCar = useIsUsedCar();
  const buttonRef = useRef<HTMLButtonElement>(null);

  const handleToastClose = () => {
    setOpenToast(false);
  };

  const handleToastClick = (title: string, name: string) => {
    setOpenToast(title === RESOURCES.SHARE_BY_COPY_LINK);

    const manufacturer = isUsedCar
      ? (car as UsedCarType).carZoneManufacturerName
      : (car as CarCardType).manufacturerDisplayName ||
        (car as CarCardType).dmvManufacturerName;

    const modelName = isUsedCar
      ? (car as UsedCarType).carZoneModelName
      : (car as CarCardType).modelNameDisplayName ||
        (car as CarCardType).dmvModelName;

    events.sendEvent(
      {
        action: EActionEvent.USER_SHARE,
        category: ECategoryEvent.USER,
      },
      {
        businessLine: businessLineTarget,
        carMake: manufacturer,
        carModel: modelName,
        carYear: car.year,
        share_type: name,
      },
    );
  };

  const shareButtonMobile = useCallback(() => {
    if (navigator.share) {
      if (buttonRef.current) {
        buttonRef.current.style.backgroundColor = COLORS.primary.light2;
      }
      try {
        const shareData = {
          url: `${window.location.href}`,
        };
        navigator.share(shareData).finally(() => {
          if (buttonRef.current) {
            buttonRef.current.style.backgroundColor = 'white';
          }
        });
      } catch (error) {
        console.error('Error sharing:', error);
      }
    }
  }, []);

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  useControlBodyOverflow({ open, isMobile });

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      if (!isMobile) {
        setAnchorEl(event.currentTarget);
      } else {
        shareButtonMobile();
      }
    },
    [isMobile, shareButtonMobile],
  );

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <div>
      <Button
        ref={buttonRef}
        startIcon={<ShareBtn />}
        id="share-button"
        aria-controls={open ? 'basic-menu' : undefined}
        aria-expanded={open}
        onClick={handleClick}
        disableRipple
        sx={{
          ...shareBtnStyle,
          ...(open && !isMobile && openShareBtnStyle),
        }}
      >
        <Typography sx={btnTextStyle}>{RESOURCES.SHARE}</Typography>
      </Button>
      <Menu
        id="share-menu"
        anchorEl={anchorEl}
        open={!isMobile && open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'share-button',
        }}
      >
        {SHARE_CAR_OPTIONS.map((item) => (
          <MenuItemDdl
            label={item.label}
            icon={item.icon}
            key={item.label}
            onClickFunc={() => {
              handleToastClick(item.label, item.name);
              item.onClickFunc();
              handleClose();
            }}
          />
        ))}
      </Menu>
      <Toast
        title={RESOURCES.SHARE_BY_COPY_LINK_TOAST_TEXT}
        open={openToast}
        handleClose={handleToastClose}
        icon={<ShareCopyLinkIcon />}
      />
    </div>
  );
};

export default ShareCarButton;
